import React from 'react';
import { List, ListContent, ListItem } from '@guildeducationinc/recess';
import ChatBubbleOutlineIcon from '@guildeducationinc/recess/icons/ChatBubbleOutlineIcon';
import MailOutlinedIcon from '@guildeducationinc/recess/icons/MailOutlinedIcon';
import SupportAgentIcon from '@guildeducationinc/recess/icons/SupportAgentIcon';
import ChatPictogram from '@guildeducationinc/recess/pictograms/ChatPictogram';
import ContactCardButton from '@src/components/GuildSupport/ContactCardButton/ContactCardButton';
import ContactCardWrapper from '@src/components/GuildSupport/ContactCardWrapper/ContactCardWrapper';
import { ComponentDataKey } from '@src/components/Pages/Home/Layout/config';
import { clickable } from '@src/analytics';
import { SALESFORCE_NEW_REQUEST_URL } from '@src/constants';
import { useEmployerContent } from '@src/context/EmployerContentContext';
import GuildChatUtils from '@src/remotes/GuildChat/GuildChatUtils';
import './GeneralContactCard.scss';

declare const window: any;

/**
 * @name GeneralContactCard
 * @description Shows general contact information for a user to call, message or chat with Guild support.
 */
const GeneralContactCard: React.FC = () => {
  const { [ComponentDataKey.GENERAL_CONTACT_CARD]: content } = useEmployerContent() ?? {};

  const handleInboundPhoneNumber = () => {
    clickable({
      loggedIn: true,
      tag: 'guild_support_inbound_phone',
      text: 'call_number',
      type: 'link',
      external_url: 'tel:+1-800-985-4027',
      product: 'home',
    });
    window.location.replace('tel:+1-800-985-4027');
  };

  const handleRedirectHelpLink = () => {
    clickable({
      loggedIn: true,
      tag: 'guild_support_email',
      text: 'send_us_a_message',
      type: 'link',
      external_url: SALESFORCE_NEW_REQUEST_URL,
      product: 'home',
    });
    window.open(SALESFORCE_NEW_REQUEST_URL, '_blank', 'noopener,noreferrer');
  };

  const CallButton = () => (
    <ContactCardButton
      Icon={SupportAgentIcon}
      onClick={handleInboundPhoneNumber}
      onKeyDown={handleInboundPhoneNumber}
      testid='callButton'
      text='Call 1-800-985-4027'
    />
  );

  const MessageButton = () => (
    <ContactCardButton
      Icon={MailOutlinedIcon}
      onClick={handleRedirectHelpLink}
      onKeyDown={handleRedirectHelpLink}
      openInNewIcon
      testid='messageButton'
      text='Send us a message'
    />
  );

  const ChatButton = () => (
    <GuildChatUtils>
      {({ openChat, isOpeningChat }) => {
        const handleOpenChat = () => {
          clickable({
            loggedIn: true,
            tag: 'guild_support_live_chat',
            text: 'lets_chat',
            type: 'link',
            product: 'home',
          });
          openChat();
        };

        return (
          <ContactCardButton
            disabled={isOpeningChat}
            Icon={ChatBubbleOutlineIcon}
            onClick={() => handleOpenChat()}
            onKeyDown={() => handleOpenChat()}
            testid='chatButton'
            text="Let's chat"
          />
        );
      }}
    </GuildChatUtils>
  );

  return (
    <ContactCardWrapper
      description={content?.description}
      heading={content?.heading}
      Icon={ChatPictogram}
    >
      <List split={false}>
        <ListContent aria-labelledby='support contact options' className='contactOptionList'>
          <ListItem className='contactOption'>
            <CallButton />
          </ListItem>
          <ListItem className='contactOption'>
            <MessageButton />
          </ListItem>
          <ListItem className='contactOption'>
            <ChatButton />
          </ListItem>
        </ListContent>
      </List>
    </ContactCardWrapper>
  );
};

export default GeneralContactCard;
